import { useState, useEffect } from 'react';
import { auth, db } from './firebase/config';
import { onAuthStateChanged } from 'firebase/auth';
import { Routes, Route, Navigate } from 'react-router-dom';
import { collection, query, where, getDocs } from 'firebase/firestore';
import LoginForm from './components/auth/LoginForm';
import Dashboard from './components/dashboard/Dashboard';
import UserManagement from './components/users/UserManagement';
import PdfGenerator from './components/certificates/PdfGenerator';
import CertificateManagement from './components/certificates/CertificateManagement';
import HistorialCertificados from './components/certificates/HistorialCertificados';
import MetricasPage from './components/dashboard/MetricasPage';
import Navbar from './components/layout/Navbar';
import ErrorBoundary from './components/common/ErrorBoundary';
import { HashRouter } from 'react-router-dom';
import { configurarUsuarioActual, limpiarUsuarioActual } from './utils/user-config';
import SecurityService from './utils/securityService';
import encryptionService from './utils/encryptionService';
import './styles/globals/App.css';

// Determina el rol por correo cuando Firestore no responde (permiso denegado)
const getFallbackRole = (email = '') => {
  const adminList = (process.env.REACT_APP_ADMIN_EMAILS || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean);
  return adminList.includes((email || '').toLowerCase()) ? 'admin' : 'usuario';
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Migrar datos existentes al formato encriptado
    encryptionService.migrateData('userSession');
    encryptionService.migrateData('userPreferences');

    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const q = query(
            collection(db, 'users'), 
            where('email', '==', firebaseUser.email)
          );
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const userData = querySnapshot.docs[0].data();
            
            const userConfig = {
              ...firebaseUser,
              rol: userData?.rol || 'usuario',
              empresa: userData?.empresa || '',
              nombre: userData?.nombre || '',
              assignedPdfs: userData?.assignedPdfs || [],
              id: userData?.id || firebaseUser.uid
            };
            
            setUser(userConfig);
            configurarUsuarioActual(userConfig);
            
            // Log de sesión iniciada
            SecurityService.logSecurityEvent('SESSION_STARTED', {
              userId: userConfig.id,
              email: userConfig.email,
              timestamp: new Date().toISOString()
            });
          } else {
            console.warn('Usuario autenticado pero no encontrado en Firestore');
            setUser(null);
            limpiarUsuarioActual();
          }
        } catch (error) {
          console.error('Error al obtener datos del usuario:', error);
          // Fallback si las reglas de Firestore bloquean la lectura
          if (error.code === 'permission-denied') {
            const userConfig = {
              ...firebaseUser,
              rol: getFallbackRole(firebaseUser.email),
              empresa: '',
              nombre: firebaseUser.displayName || firebaseUser.email,
              assignedPdfs: [],
              id: firebaseUser.uid
            };

            setUser(userConfig);
            configurarUsuarioActual(userConfig);
            SecurityService.logSecurityEvent('USER_DATA_PERMISSION_FALLBACK', {
              email: firebaseUser.email,
              timestamp: new Date().toISOString()
            });
          } else {
            SecurityService.logSecurityEvent('USER_DATA_ERROR', {
              error: error.message,
              email: firebaseUser.email,
              timestamp: new Date().toISOString()
            });
            setUser(null);
            limpiarUsuarioActual();
          }
        }
      } else {
        setUser(null);
        limpiarUsuarioActual();
        
        // Limpiar datos de sesión
        encryptionService.removeItem('userSession');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Manejo seguro del login
  const handleLogin = (userData) => {
    try {
      const userConfig = {
        ...userData,
        rol: userData?.rol || 'usuario',
        empresa: userData?.empresa || '',
        nombre: userData?.nombre || '',
        assignedPdfs: userData?.assignedPdfs || [],
        id: userData?.id || userData?.uid
      };
      
      setUser(userConfig);
      configurarUsuarioActual(userConfig);
    } catch (error) {
      console.error('Error al configurar usuario:', error);
      SecurityService.logSecurityEvent('LOGIN_CONFIG_ERROR', {
        error: error.message,
        timestamp: new Date().toISOString()
      });
    }
  };

  // Actualizar título dinámicamente
  useEffect(() => {
    if (user) {
      document.title = `Andes Mining App - ${user.nombre || user.email}`;
    } else {
      document.title = 'Andes Mining App';
    }
  }, [user]);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <ErrorBoundary>
      <HashRouter>
        <div className="App">
          {!user ? (
            <LoginForm onLogin={handleLogin} />
          ) : (
            <>
              <Navbar user={user} />
              <div className="app-content">
                <ErrorBoundary>
                  <Routes>
                    <Route path="/" element={<Dashboard user={user} />} />
                    <Route path="/dashboard" element={<Dashboard user={user} />} />
                    <Route path="/historial" element={<HistorialCertificados user={user} />} />
                    <Route 
                      path="/generar-pdf" 
                      element={
                        user.rol === 'admin' 
                          ? <PdfGenerator user={user} />
                          : <Navigate to="/dashboard" replace />
                      } 
                    />
                    <Route 
                      path="/usuarios" 
                      element={
                        user.rol === 'admin' 
                          ? <UserManagement user={user} />
                          : <Navigate to="/dashboard" replace />
                      } 
                    />
                    <Route 
                      path="/certificados" 
                      element={
                        user.rol === 'admin' 
                          ? <CertificateManagement user={user} />
                          : <Navigate to="/dashboard" replace />
                      } 
                    />
                    <Route 
                      path="/metricas" 
                      element={
                        user.rol === 'admin' 
                          ? <MetricasPage user={user} />
                          : <Navigate to="/dashboard" replace />
                      } 
                    />
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                </ErrorBoundary>
              </div>
            </>
          )}
        </div>
      </HashRouter>
    </ErrorBoundary>
  );
}

export default App;